// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Hard stop shown when the intake is flagged as a possible emergency — either by the
// backend/ai-gateway red-flag interceptor (authoritative, conversation status
// "emergency") or the client-side keyword safety net. Full-screen, non-dismissible; on
// the in-clinic kiosk it stays locked so staff can assist. Self-service patients get a
// small "not an emergency" path back to the check-in in case of a false positive.

import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Phone, AlertTriangle } from 'lucide-react'
import { isKioskSession } from '@/lib/kiosk-mode'

export function EmergencyScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const kiosk = isKioskSession()

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6 py-10 bg-red-600 text-white overflow-y-auto">
      <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-6">
        <AlertTriangle className="w-9 h-9 text-white" />
      </div>

      <h1 className="text-2xl font-bold text-center mb-3">{t('emergency.title')}</h1>
      <p className="text-[15px] text-red-50 text-center leading-relaxed max-w-md mb-8">
        {t('emergency.body')}
      </p>

      <a
        href="tel:911"
        className="w-full max-w-sm h-14 rounded-2xl bg-white text-red-600 font-bold text-lg flex items-center justify-center gap-2.5 hover:bg-red-50 active:scale-[0.98] transition-all shadow-lg"
      >
        <Phone className="w-5 h-5" />
        {t('emergency.call911')}
      </a>

      <p className="text-[13px] text-red-100 text-center leading-relaxed max-w-sm mt-6">
        {t('emergency.clinicNote')}
      </p>

      {!kiosk && (
        <button
          onClick={() => navigate('/chat')}
          className="mt-10 text-[13px] font-medium text-red-100/80 underline hover:text-white transition-colors"
        >
          {t('emergency.return')}
        </button>
      )}
    </div>
  )
}
